import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Shield, Scale, Globe } from 'lucide-react';

const WhyNeedSection = () => {
  const risks = [
    { icon: AlertTriangle, title: 'Anyone Can Copy Your Logo', desc: 'Without trademark registration, your logo design has zero legal protection. Competitors and counterfeiters can freely replicate your brand identity.' },
    { icon: Scale, title: 'No Legal Rights Without Registration', desc: 'Using a logo doesn\'t automatically give you legal ownership. Only USPTO registration provides enforceable federal trademark rights.' },
    { icon: Shield, title: 'Risk of Brand Theft & Lawsuits', desc: 'Unprotected logos are vulnerable to theft. Worse, someone else could register a similar mark and sue you for using your own logo.' },
  ];

  const keyPoints = [
    { icon: Shield, title: 'Legal Ownership', desc: 'Official federal trademark registration gives you documented proof of ownership' },
    { icon: Scale, title: 'Exclusive Rights', desc: 'Exclusive nationwide rights to use your logo in commerce with legal enforcement' },
    { icon: Globe, title: 'Nationwide Protection', desc: 'Federal protection across all 50 states and US territories from day of filing' },
  ];

  return (
    <section data-testid="why-need-section" className="py-28 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-[300px] h-[300px] bg-[#3B82F6]/[0.04] rounded-full blur-[80px]" />
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div className="max-w-3xl mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 mb-5">
            <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
            <span className="text-xs font-semibold text-red-400 uppercase tracking-wider">Don't Risk Your Brand</span>
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-white mb-5">
            Why Logo Trademark Protection <span className="text-gradient">Matters</span>
          </h2>
          <p className="text-[#A1A1AA] leading-relaxed text-lg">
            Your logo is the face of your brand. Without proper trademark registration, it has no legal protection.
          </p>
        </motion.div>

        {/* Risks */}
        <div className="grid md:grid-cols-3 gap-4 mb-16">
          {risks.map((risk, index) => (
            <motion.div key={risk.title} className="glass-card rounded-2xl p-7 border-red-500/10 hover:border-red-500/20 transition-all"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
              <div className="w-11 h-11 rounded-xl bg-red-500/10 flex items-center justify-center mb-5 icon-hover">
                <risk.icon className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-white mb-3">{risk.title}</h3>
              <p className="text-sm text-[#A1A1AA] leading-relaxed">{risk.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Key Points */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <h3 className="font-heading text-2xl font-semibold text-white mb-8">With Trademark Registration, You Get:</h3>
          <div className="grid md:grid-cols-3 gap-4">
            {keyPoints.map((point, index) => (
              <div key={point.title} className="glass-card rounded-2xl p-7 border-[#3B82F6]/10 hover:border-[#3B82F6]/30 transition-all">
                <div className="w-11 h-11 rounded-xl bg-[#3B82F6]/10 flex items-center justify-center mb-5">
                  <point.icon className="w-5 h-5 text-[#3B82F6]" />
                </div>
                <h4 className="font-heading text-lg font-semibold text-white mb-2">{point.title}</h4>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">{point.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyNeedSection;
